import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './CustomerList.module.scss';
import classNames from 'classnames/bind';
import { BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/customer/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { customerActions } from '~/thunks/customer/customerSlice';
import { Link } from '@mui/material';
import { ICustomer, ICustomerList } from '~/utils/interface/customer';
import { useNavigate } from 'react-router-dom';
import { adminRouteAbsolute } from '~/utils/constants/route';
import { PERMISSIONS } from '~/utils/constants/permission';
const cx = classNames.bind(styles);

type CustomerListProps = {
  data?: ICustomer;
  onOpenEdit: (id: string) => void;
};

function CustomerList(props: CustomerListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/customer']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'name',
        label: t('properties.name'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'email',
        label: t('properties.email'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'phone',
        label: t('properties.phone'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'address',
        label: t('properties.address'),
        sortable: true,
        searchable: false,
      },

      {
        id: 'quickbookCustomer',
        label: t('properties.quickbookConnected'),
        sortable: true,
        searchable: false,
      },
    ],
    [t]
  );
  const navigate = useNavigate();
  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { customers, filter, currentPage, totalPages } = useAppSelector((state) => state.customer);
  const dispatch = useAppDispatch();

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(customerActions.changeCustomerId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(customerActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(customerActions.changeMenuSelectId(menuSelectedId));
      dispatch(customerActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      customerActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      customerActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };
  return (
    <Box className={cx('cusomer-container')}>
      <BaseTable<ICustomerList>
        data={customers}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(customer: ICustomerList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('container')}>
                  <Box className={cx('titleColumn')}>
                    {' '}
                    <Link
                      onClick={() => navigate(adminRouteAbsolute.customerDetail.replace(':id', customer.id))}
                      className='text-[#3A3F51] hover:text-[#007AFC]'
                    >
                      {customer.name}
                    </Link>
                  </Box>
                </Box>
              </TableCell>

              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('container')}>
                  <Box className={cx('titleColumn')}>{customer.email}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('container')}>
                  <Box className={cx('titleColumn')}>{customer.phone}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('container')}>
                  <Box className={cx('titleColumn')}>{customer.fullAddress}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('container')}>
                  <Box className={cx('titleColumn')}>{customer.quickbookCustomer ? t('yes') : t('no')}</Box>
                </Box>
              </TableCell>
              {(permissions[PERMISSIONS.organization_edit] || permissions[PERMISSIONS.organization_delete]) && (
                <TableCell>
                  <Box className={cx('deadline-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, customer.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        enableEdit={!!permissions[PERMISSIONS.organization_edit]}
        enableDelete={!!permissions[PERMISSIONS.organization_delete]}
      />
    </Box>
  );
}

export default memo(CustomerList);
